import { FormHelperText, Grid } from "@mui/material";
import { AppImages } from "app/appImages";
import { CorePermissions } from "app/appPermissions";
import { MprcContainer, MprcFormSubHeader } from "components";
import { AutoCompleteDataModel } from "components/MprcAutoComplete";
import { createAutoCompleteModel } from "components/MprcAutoComplete/AutoCompleteDataModel";
import { AutoCompleteHierarchicalModel, UnitAutoCompleteHierarchicalModel } from "components/MprcAutoCompleteHierarchical/AutoCompleteHierarchicalModel";
import { MprcDatePicker } from "components/MprcDatePicker";
import { MprcStringToHtml } from "components/MprcStringToHtml";
import { CustomFieldFillConstraint, CustomFieldValueDto, ManagedUserQueryResponse, SecondaryManagerItemDto, UnitAutoCompleteHierarchicalModelDto, UnitTypeItemDto, UserDto, UserSelectionEntityType } from "generated/CoreApi_v1";
import moment from "moment";
import { useSnackbar } from "notistack";
import { useSession } from "providers/SessionProvider";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CustomFieldInput, getCustomFieldGridSize } from "views/foundation/customField/components/CustomFieldInput";
import { UnitAutoCompleteHierarchical } from "views/foundation/unit/components/UnitAutoCompleteHierarchical";
import { useEditUserContext } from "views/foundation/user/Home/EditUser/EditUser";
import { UserAutoComplete } from "../../components/UserAutoComplete";

////////////////////////////////////////////////////////////////////////////

interface IValidationFields {
    admissionDate: boolean;
    dismissedDate: boolean;
    units: { [key: string]: boolean; };
    customFields: { [key: string]: boolean; };
}

export interface IPostValidation {
    customFields: { [key: string]: string[] | undefined; };
    secondaryManagers: { [key: string]: string[] | undefined; };
    unitTypes: { [key: string]: string[] | undefined; };
    units: string[] | undefined;
    managerId: string[] | undefined;
    approverId: string[] | undefined;
    admissionDate: string[] | undefined;
    dismissedDate: string[] | undefined;
}

export interface IUserManagementInfoBoxData {
    userId: string;
    customFieldsCorporate: CustomFieldValueDto[] | null;
    unitTypes: UnitTypeItemDto[];
    secondaryManagers: SecondaryManagerItemDto[] | null;
    manager: UserDto | null;
    approver: UserDto | null;
    admissionDate: Date | null;
    dismissedDate: Date | null;
}
interface IProps {
    viewData: IUserManagementInfoBoxData;
    onFormValidation: (valid: boolean) => void;
}
////////////////////////////////////////////////////////////////////////////

///
function getDefaultValidateUnits(dto: Partial<IUserManagementInfoBoxData>): { [key: string]: boolean; } {
    let objUnits: { [key: string]: boolean; } = {};

    dto.unitTypes
        ?.filter((x) => x.isRequired)
        .forEach((u) => (objUnits[u.unitTypeName] = u.units !== undefined && u.units !== null && u.units.length > 0));

    return objUnits;
}

///
function getDefaultValidate(dto: Partial<ManagedUserQueryResponse>): IValidationFields {
    let obj = { units: {}, customFields: {}, admissionDate: true, dismissedDate: true } as IValidationFields;

    dto.customFieldsCorporate
        ?.filter((x) => x.isRequired)
        .forEach((f) => (obj.customFields[f.fieldId] = f.fieldValue != undefined && f.fieldValue.length > 0));

    obj.units = getDefaultValidateUnits(dto);

    return obj;
}

////////////////////////////////////////////////////////////////////////////

/**
 * Edição de Usuário - Informações Gerenciais
 * @param props
 */
export const EditUserManagementInfoBox: React.FC<IProps> = (props) => {

    const { t } = useTranslation();
    const { permissionExplorer } = useSession();
    const { enqueueSnackbar } = useSnackbar();

    const { viewData, onFormValidation } = props;
    const { viewErrors, isNewUser, readonly, onFormUpdate, onError } = useEditUserContext();

    const [formValidation, setFormValidation] = useState<IValidationFields>(getDefaultValidate(viewData));
    const [postValidation, setPostValidation] = useState<IPostValidation>({
        customFields: {},
        secondaryManagers: {},
        unitTypes: {},
    } as IPostValidation);

    const corporateNonConfidencialFields = (viewData.customFieldsCorporate ?? []).filter(
        (x) => x.fillConstraint != CustomFieldFillConstraint.Confidential
    );
    const corporateConfidencialFields = (viewData.customFieldsCorporate ?? []).filter(
        (x) => x.fillConstraint == CustomFieldFillConstraint.Confidential
    );

    ////////////////////////////////////////////////////////////////////////////

    ///
    function handleUpdateRecord(name: keyof ManagedUserQueryResponse, v: any) {
        if (name === "unitTypes") {
            setFormValidation({ ...formValidation, units: getDefaultValidateUnits({ ...viewData, [name]: v }) });
        } else if (name === "admissionDate") {
            setFormValidation({
                ...formValidation,
                admissionDate: v == null || (moment(v).isValid() && (viewData.dismissedDate == null || moment(viewData.dismissedDate) >= moment(v))),
                dismissedDate: viewData.dismissedDate == null || (moment(viewData.dismissedDate).isValid() && (v == null || moment(v) <= moment(viewData.dismissedDate))),
            });
        } else if (name === "dismissedDate") {
            setFormValidation({
                ...formValidation,
                dismissedDate: v == null || (moment(v).isValid() && (viewData.admissionDate == null || moment(viewData.admissionDate) <= moment(v))),
                admissionDate: viewData.admissionDate == null || (moment(viewData.admissionDate).isValid() && (v == null || moment(v) >= moment(viewData.admissionDate)))
            });
        }

        onFormUpdate({ [name]: v });
    }

    ///
    const handleChangeUnit = (unitTypeId: string, items: (AutoCompleteHierarchicalModel)[]) => {
        let newItems = viewData.unitTypes.map((x) => {
            if (x.unitTypeId === unitTypeId) {
                x.units = items as UnitAutoCompleteHierarchicalModelDto[];
            }
            return x;
        });

        handleUpdateRecord("unitTypes", newItems);
    };

    ///
    const handleOnChangeManager = (type: "manager" | "approver", items: AutoCompleteDataModel[]) => {
        if (items.length > 0) {
            if (items[0].key == viewData.userId) {
                enqueueSnackbar(
                    t(type == "manager" ? "User.Edit.CannotManageYourself" : "User.Edit.CannotApproveYourself"),
                    { variant: "error" }
                );
            }
            else {
                handleUpdateRecord(type, {
                    userId: items[0].key,
                    name: items[0].label,
                    thumbnailUrl: items[0].thumbnailUrl ?? AppImages.UserThumbnail
                } as UserDto);
            }
        } else {
            handleUpdateRecord(type, undefined);
        }
    };

    ///
    const handleOnChangeSecondaryManager = (managerTypeId: string, items: AutoCompleteDataModel[]) => {
        let newItems = viewData.secondaryManagers?.map((x) => {
            if (x.managerTypeId === managerTypeId) {
                if (items.length > 0) {
                    if (items[0].key == viewData.userId) {
                        enqueueSnackbar(t("User.Edit.CannotManageYourself"), { variant: "error" });
                    }
                    else {
                        x.manager = {
                            userId: items[0].key,
                            name: items[0].label,
                            thumbnailUrl: items[0].thumbnailUrl ?? AppImages.UserThumbnail
                        };
                    }
                } else {
                    x.manager = null;
                }
            }
            return x;
        });

        handleUpdateRecord("secondaryManagers", newItems);
    };

    ///
    const getUnitSelected = (unitTypeId: string): UnitAutoCompleteHierarchicalModel[] => {
        const unitType = viewData.unitTypes.find((x) => x.unitTypeId === unitTypeId);

        if (unitType && unitType.units)
            return unitType.units!.map((u) =>
                (u as UnitAutoCompleteHierarchicalModel));

        return [];
    };

    ///
    const handleUpdateCustomField = (field: CustomFieldValueDto, value: any, isValid: boolean) => {
        let newCustomFields = viewData.customFieldsCorporate?.map((item) => {
            if (item.fieldId === field.fieldId) item.fieldValue = value;
            return item;
        });

        handleUpdateRecord("customFieldsCorporate", newCustomFields);

        if ((field.fieldValue != undefined) || formValidation.customFields[field.fieldId] === false)
            setFormValidation({
                ...formValidation,
                customFields: { ...formValidation.customFields, [field.fieldId]: isValid }
            });
    };

    ////////////////////////////////////////////////////////////////////////////

    useEffect(() => {
        let unitsNotDefined = viewData.unitTypes?.filter((x) => x.units !== undefined).length === 0;

        //Se unidades ainda não forem definidos, não atualiza o estado do formulário de validação
        if (!unitsNotDefined || !formValidation.admissionDate || !formValidation.dismissedDate) {
            onFormValidation(
                formValidation.admissionDate &&
                formValidation.dismissedDate &&
                Object.values(formValidation.units).filter((x) => x === false).length === 0 &&
                Object.values(formValidation.customFields).filter((x) => x === false).length === 0
            );
        }
    }, [formValidation]);

    useEffect(() => {
        if (viewErrors != null) {

            const postValidationFields: { [key: string]: string[]; } = {};
            const postValidationUnits: { [key: string]: string[]; } = {};
            const postValidationManagers: { [key: string]: string[]; } = {};

            setFormValidation(state => {

                var customFields = viewData.customFieldsCorporate?.reduce(
                    (accumulator, currValue) => {
                        if (viewErrors.hasOwnProperty(`customFields.${currValue.fieldId}`)) {
                            accumulator[currValue.fieldId] = false;
                            postValidationFields[currValue.fieldId] = viewErrors[`customFields.${currValue.fieldId}`];
                        }
                        return accumulator;
                    }, {} as { [key: string]: boolean; }) ?? {};

                var unitTypes = viewData.unitTypes?.reduce(
                    (accumulator, currValue) => {
                        if (viewErrors.hasOwnProperty(`units.${currValue.unitTypeName}`)) {
                            accumulator[currValue.unitTypeName] = false;
                            postValidationUnits[currValue.unitTypeName] = viewErrors[`units.${currValue.unitTypeName}`];
                        }
                        return accumulator;
                    }, {} as { [key: string]: boolean; }) ?? {};

                var secondaryManagers = viewData.secondaryManagers?.reduce(
                    (accumulator, currValue) => {
                        if (viewErrors.hasOwnProperty(`secondaryManagers.${currValue.managerTypeId}`)) {
                            accumulator[currValue.managerTypeId] = false;
                            postValidationManagers[currValue.managerTypeId] = viewErrors[`secondaryManagers.${currValue.managerTypeId}`];
                        }
                        return accumulator;
                    }, {} as { [key: string]: boolean; }) ?? {};

                return {
                    ...state,
                    customFields: customFields,
                    secondaryManagers: secondaryManagers,
                    units: unitTypes,
                    managerId: viewErrors["managerId"] === undefined,
                    approverId: viewErrors["approverId"] === undefined,
                    admissionDate: viewErrors["admissionDate"] === undefined,
                    dismissedDate: viewErrors["dismissedDate"] === undefined
                };
            }
            );

            setPostValidation(state => ({
                ...state,
                managerId: viewErrors["managerId"],
                approverId: viewErrors["managerId"],
                admissionDate: viewErrors["admissionDate"],
                dismissedDate: viewErrors["dismissedDate"],
                customFields: postValidationFields,
                secondaryManagers: postValidationManagers,
                unitTypes: postValidationUnits,
                units: viewErrors["units"]
            }));

        }
    }, [viewErrors]);

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    return (
        <>
            <MprcContainer margin="large">
                <MprcFormSubHeader
                    title={t("SysObjects.Units", { ns: "shared" })}
                    subtitle={
                        <FormHelperText error={postValidation["units"] != undefined}>
                            {postValidation["units"] ? <MprcStringToHtml text={postValidation["units"] ?? ""} color="error" /> : undefined}
                        </FormHelperText>
                    }
                />
                {!viewData.unitTypes
                    ? null
                    : viewData.unitTypes.map((unitType) => (
                        <UnitAutoCompleteHierarchical
                            margin="dense"
                            key={unitType.unitTypeId}
                            label={unitType.unitTypeName}
                            required={unitType.isRequired}
                            multiSelect={unitType.allowMultiSelect}
                            disabled={readonly}
                            selectedMode="assignment"
                            selectedItems={getUnitSelected(unitType.unitTypeId)}
                            onChange={(item) => handleChangeUnit(unitType.unitTypeId, item)}
                            onError={error => onError(error)}
                            unitTypeId={unitType.unitTypeId}
                            unitTypeName={unitType.unitTypeName}
                            error={
                                (unitType.units !== undefined && formValidation.units[unitType.unitTypeName] === false)
                                || postValidation.unitTypes[unitType.unitTypeName] != undefined
                            }
                            helperText={
                                <MprcStringToHtml text={postValidation.unitTypes[unitType.unitTypeName] ?? ""} color="error" />
                            }
                        />
                    ))}
            </MprcContainer>

            <MprcContainer margin="large">
                <MprcFormSubHeader
                    margin="dense"
                    title={t("User.Edit.ManagementSubtitle")}
                />

                <Grid container rowSpacing={1}>
                    <Grid size={{ xs: 12 }}>
                        <UserAutoComplete
                            label={t("User.Edit.ManagerLabel")}
                            placeholder={t("User.Edit.ManagerPlaceholder")}
                            multiSelect={false}
                            includeYourself={true}
                            disabled={readonly}
                            selectedItems={viewData.manager
                                ? [
                                    createAutoCompleteModel(
                                        viewData.manager.userId,
                                        viewData.manager.name,
                                        viewData.manager.thumbnailUrl
                                    )
                                ]
                                : []}
                            onChange={(items) => handleOnChangeManager("manager", items)}
                            onError={onError}
                            error={postValidation["managerId"] != undefined}
                            helperText={
                                <MprcStringToHtml text={postValidation["managerId"] ?? ""} color="error" />
                            }
                            selectionHistoryType={UserSelectionEntityType.UserManager}
                        />
                    </Grid>
                    {
                        !viewData.secondaryManagers
                            ? null
                            : viewData.secondaryManagers.map((x) => (
                                <Grid size={{ xs: 12 }} key={x.managerTypeId}>
                                    <UserAutoComplete
                                        label={x.managerTypeName}
                                        placeholder={t("User.Edit.ManagerPlaceholder")}
                                        multiSelect={false}
                                        includeYourself={true}
                                        disabled={readonly}
                                        selectedItems={
                                            x.manager != null
                                                ? [
                                                    {
                                                        label: x.manager.name,
                                                        key: x.manager.userId,
                                                        canDelete: true,
                                                        thumbnailUrl: x.manager.thumbnailUrl
                                                    } as AutoCompleteDataModel
                                                ]
                                                : []
                                        }
                                        onChange={(items) => handleOnChangeSecondaryManager(x.managerTypeId, items)}
                                        onError={onError}
                                        error={postValidation.secondaryManagers[x.managerTypeId] != undefined}
                                        helperText={
                                            <MprcStringToHtml
                                                text={postValidation.secondaryManagers[x.managerTypeId]}
                                                color="error"
                                            />
                                        }
                                        selectionHistoryType={UserSelectionEntityType.UserManager}
                                    />
                                </Grid>
                            ))
                    }
                    <Grid size={{ xs: 12 }}>
                        <UserAutoComplete
                            label={t("User.Edit.ApproverLabel")}
                            placeholder={t("User.Edit.ManagerPlaceholder")}
                            multiSelect={false}
                            includeYourself={true}
                            disabled={readonly}
                            selectedItems={
                                viewData.approver
                                    ? [
                                        createAutoCompleteModel(
                                            viewData.approver.userId,
                                            viewData.approver.name,
                                            viewData.approver.thumbnailUrl
                                        )
                                    ]
                                    : []
                            }
                            onError={onError}
                            onChange={(items) => handleOnChangeManager("approver", items)}
                            error={postValidation["approverId"] != undefined}
                            helperText={
                                postValidation["approverId"] ? (
                                    <MprcStringToHtml text={postValidation["approverId"] ?? ""} color="error" />
                                ) : undefined
                            }
                            selectionHistoryType={UserSelectionEntityType.UserApprover}
                        />
                    </Grid>
                </Grid>
            </MprcContainer>

            <MprcContainer margin="large">
                <MprcFormSubHeader
                    margin="dense"
                    title={t("User.Edit.PeriodAsCollaborator")}
                />
                <Grid container spacing={4}>
                    <Grid>
                        <MprcDatePicker
                            label={t("User.Edit.AdmissionDateLabel")}
                            value={viewData.admissionDate}
                            disabled={readonly}
                            onChange={(date) => handleUpdateRecord("admissionDate", date)}
                            error={postValidation?.admissionDate != undefined || !formValidation.admissionDate}
                            helperText={<MprcStringToHtml text={postValidation?.admissionDate} color="error" />}
                            width={170}
                        />
                    </Grid>
                    <Grid>
                        <MprcDatePicker
                            label={t("User.Edit.DismissedDateLabel")}
                            value={viewData.dismissedDate}
                            disabled={readonly}
                            onChange={(date) => handleUpdateRecord("dismissedDate", date)}
                            error={postValidation?.dismissedDate != undefined || !formValidation.dismissedDate}
                            helperText={<MprcStringToHtml text={postValidation?.dismissedDate} color="error" />}
                            width={190}
                        />
                    </Grid>
                </Grid>
            </MprcContainer>

            {corporateNonConfidencialFields && corporateNonConfidencialFields.length > 0 && (
                <MprcContainer margin="large">
                    <MprcFormSubHeader
                        margin="dense"
                        title={t("User.Edit.OtherManagementInfoSubtitle")}
                    />
                    <Grid container columnSpacing={4}>
                        {corporateNonConfidencialFields.map((item, i) => (
                            <Grid key={item.fieldId} size={{ xs: getCustomFieldGridSize(item.showWidth) }}>
                                <CustomFieldInput
                                    margin="dense"
                                    field={item}
                                    disabled={readonly}
                                    useDefaultValue={isNewUser}
                                    onChange={(value, formattedValue, isValid) => handleUpdateCustomField(item, value, isValid)}
                                    error={postValidation.customFields[item.fieldId]}
                                    validateOnFirstMount={true}
                                />
                            </Grid>
                        ))}
                    </Grid>
                </MprcContainer>
            )}

            {permissionExplorer.hasOne([CorePermissions.UsersViewConfidential, CorePermissions.UsersUpdateConfidential])
                && corporateConfidencialFields && corporateConfidencialFields?.length > 0 && (
                    <MprcContainer margin="large">
                        <MprcFormSubHeader
                            titleSx={{ color: "warning.main" }}
                            title={t("User.Edit.ConfidentialInfoSubtitle")}
                        />
                        <Grid container columnSpacing={4}>
                            {corporateConfidencialFields.map((item, i) => (
                                <Grid key={i} size={{ xs: getCustomFieldGridSize(item.showWidth) }}>
                                    <CustomFieldInput
                                        field={
                                            {
                                                ...item,
                                                fillConstraint: !permissionExplorer.hasOne([CorePermissions.UsersUpdateConfidential]) ?
                                                    CustomFieldFillConstraint.Readonly :
                                                    item.fillConstraint
                                            }}
                                        onChange={(value, formattedValue, isValid) => handleUpdateCustomField(item, value, isValid)}
                                        useDefaultValue={isNewUser}
                                        error={postValidation.customFields[item.fieldId]}
                                    />
                                    {formValidation.customFields[item.fieldId]}
                                </Grid>
                            ))}
                        </Grid>
                    </MprcContainer>
                )}
        </>
    );
};
